import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  Group,
  Stack,
  Table,
  Title,
  Text,
  Badge,
  Button,
  Loader,
  Center,
  Alert,
  Collapse,
  TextInput,
  Select,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconChevronRight,
  IconPlus,
  IconFilter,
  IconFilterOff,
  IconSearch,
} from '@tabler/icons-react';
import { merchantsApi } from '../api/merchants';
import { formatGmt7, formatBps, formatIdr } from '../lib/format';
import { describeError } from '../api/client';
import { DataTable } from '../components/DataTable';
import { useTranslation } from '../i18n/LocaleContext';

type MerchantFilter = {
  search?: string;
  status?: 'active' | 'suspended';
  api?: 'enabled' | 'disabled';
};

// Human labels for disbursement beneficiary account types (raw keys BANK/WALLET/BPR).
const DISB_TYPE_LABEL: Record<string, string> = {
  BANK: 'Bank',
  WALLET: 'E-wallet',
  BPR: 'BPR',
};

type FeeOverrideRow = { label: string; pct?: string; amount: string };

/**
 * Chip shown next to a merchant's DEFAULT fee in the list when per-type overrides
 * exist (VA per-code cash-in fees, or per-account-type disbursement fees). The
 * hover panel uses a "default + override" layout: the base rate on top, then each
 * override below a divider — so the relationship between the global default and
 * the per-type overrides reads at a glance. Informational only; overrides are
 * configured in the merchant detail tabs. Accent: blue = VA, grape = disbursement.
 */
function FeeOverrideBadge({
  accent,
  chipLabel,
  title,
  subtitle,
  defaultLabel,
  defaultValue,
  rows,
}: {
  accent: 'blue' | 'grape';
  chipLabel: string;
  title: string;
  subtitle: string;
  defaultLabel: string;
  defaultValue: string;
  rows: FeeOverrideRow[];
}) {
  return (
    <Tooltip
      withArrow
      arrowSize={8}
      position="top"
      transitionProps={{ transition: 'pop', duration: 120 }}
      styles={{
        tooltip: {
          background: 'var(--mantine-color-body)',
          color: 'var(--mantine-color-text)',
          border: '1px solid var(--mantine-color-default-border)',
          boxShadow: 'var(--mantine-shadow-md)',
          borderRadius: 'var(--mantine-radius-md)',
          padding: '10px 12px',
          maxWidth: 'none',
          whiteSpace: 'normal',
        },
        arrow: { background: 'var(--mantine-color-body)' },
      }}
      label={
        <Box miw={186}>
          <Group gap={7} mb={8} wrap="nowrap">
            <Box
              w={7}
              h={7}
              style={{
                flex: '0 0 auto',
                borderRadius: 2,
                background: `var(--mantine-color-${accent}-5)`,
              }}
            />
            <Text tt="uppercase" fw={700} fz={10} lh={1.2} style={{ letterSpacing: '0.08em' }}>
              {title}
            </Text>
            <Text c="dimmed" fz={11} lh={1.2}>
              · {subtitle}
            </Text>
          </Group>
          <Group
            justify="space-between"
            gap={20}
            pb={7}
            mb={7}
            wrap="nowrap"
            style={{ borderBottom: '1px dashed var(--mantine-color-default-border)' }}
          >
            <Text c="dimmed" fz={12}>
              {defaultLabel}
            </Text>
            <Text fw={600} fz={12} style={{ fontVariantNumeric: 'tabular-nums' }}>
              {defaultValue}
            </Text>
          </Group>
          <Stack gap={4}>
            {rows.map((r) => (
              <Group key={r.label} justify="space-between" gap={20} wrap="nowrap">
                <Text ff="monospace" fz="xs">
                  {r.label}
                </Text>
                <Text fz="xs" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {r.pct ? (
                    <>
                      <Text component="span" c={accent} fw={600} fz="xs">
                        {r.pct}
                      </Text>
                      {' + '}
                      {r.amount}
                    </>
                  ) : (
                    <Text component="span" fw={600} fz="xs">
                      {r.amount}
                    </Text>
                  )}
                </Text>
              </Group>
            ))}
          </Stack>
        </Box>
      }
    >
      <Badge size="xs" variant="light" color={accent} style={{ cursor: 'help' }}>
        {chipLabel}
      </Badge>
    </Tooltip>
  );
}

export function MerchantsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [pageIdx, setPageIdx] = useState(0);
  // Draft = the form's pending buffer; applied = what's actually queried (so
  // typing in the search box doesn't refetch until "Apply").
  const [draft, setDraft] = useState({ search: '', status: '', api: '' });
  const [applied, setApplied] = useState<MerchantFilter>({});
  const [filtersOpen, setFiltersOpen] = useState(true);

  const { data, isLoading, error } = useQuery({
    queryKey: ['merchants', applied, cursorStack[pageIdx]],
    queryFn: () =>
      merchantsApi.list({ cursor: cursorStack[pageIdx] ?? undefined, limit: 25, ...applied }),
    retry: 0,
    placeholderData: (prev) => prev,
  });

  const activeCount = (applied.search ? 1 : 0) + (applied.status ? 1 : 0) + (applied.api ? 1 : 0);

  const applyFilters = () => {
    setApplied({
      ...(draft.search.trim() ? { search: draft.search.trim() } : {}),
      ...(draft.status ? { status: draft.status as 'active' | 'suspended' } : {}),
      ...(draft.api ? { api: draft.api as 'enabled' | 'disabled' } : {}),
    });
    setCursorStack([null]);
    setPageIdx(0);
  };

  const clearFilters = () => {
    setDraft({ search: '', status: '', api: '' });
    setApplied({});
    setCursorStack([null]);
    setPageIdx(0);
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>{t('merchants.title')}</Title>
          <Text c="dimmed" size="sm">
            {t('merchants.subtitle')}
          </Text>
        </div>
        <Button component={Link} to="/dashboard/merchants/new" leftSection={<IconPlus size={14} />}>
          {t('merchants.newMerchant')}
        </Button>
      </Group>

      <Card withBorder p="md">
        <Group justify="space-between" mb={filtersOpen ? 'sm' : 0}>
          <Group gap="xs">
            <Text fw={600} size="sm">{t('common.filters')}</Text>
            {activeCount > 0 && (
              <Badge size="sm" variant="light">{t('merchants.activeFilters', { n: activeCount })}</Badge>
            )}
          </Group>
          <Button
            variant="subtle"
            size="xs"
            onClick={() => setFiltersOpen((o) => !o)}
            leftSection={filtersOpen ? <IconFilterOff size={14} /> : <IconFilter size={14} />}
          >
            {filtersOpen ? t('common.collapse') : t('common.show')}
          </Button>
        </Group>
        <Collapse in={filtersOpen}>
          <Group align="end" wrap="wrap" gap="sm">
            <TextInput
              label={t('merchants.filterSearch')}
              placeholder={t('merchants.filterSearchPh')}
              leftSection={<IconSearch size={14} />}
              value={draft.search}
              onChange={(e) => setDraft((d) => ({ ...d, search: e.currentTarget.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyFilters();
              }}
              style={{ flex: 1, minWidth: 220 }}
            />
            <Select
              label={t('merchants.filterStatus')}
              placeholder={t('merchants.filterAll')}
              clearable
              data={[
                { value: 'active', label: t('merchants.statusActive') },
                { value: 'suspended', label: t('merchants.statusSuspended') },
              ]}
              value={draft.status || null}
              onChange={(v) => setDraft((d) => ({ ...d, status: v ?? '' }))}
              w={170}
            />
            <Select
              label={t('merchants.filterApi')}
              placeholder={t('merchants.filterAll')}
              clearable
              data={[
                { value: 'enabled', label: t('merchants.apiEnabled') },
                { value: 'disabled', label: t('merchants.apiDisabled') },
              ]}
              value={draft.api || null}
              onChange={(v) => setDraft((d) => ({ ...d, api: v ?? '' }))}
              w={170}
            />
            <Button variant="default" onClick={clearFilters}>
              {t('common.reset')}
            </Button>
            <Button onClick={applyFilters}>{t('common.apply')}</Button>
          </Group>
        </Collapse>
      </Card>

      <Card p={0}>
        {isLoading && (
          <Center py="xl">
            <Loader />
          </Center>
        )}
        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} m="md">
            {describeError(error)}
          </Alert>
        )}
        {!isLoading && !error && (
          <DataTable
            tableId="merchants"
            columns={[
              { key: 'id', label: t('merchants.colId'), width: 120, sticky: 'left' },
              { key: 'name', label: t('merchants.colName'), width: 210 },
              { key: 'email', label: t('merchants.colEmail'), width: 230 },
              { key: 'depositFee', label: t('merchants.colDepositFee'), width: 110 },
              { key: 'disbFee', label: t('merchants.colDisbFee'), width: 110 },
              { key: 'active', label: t('merchants.colActiveBalance'), width: 150, align: 'right' },
              { key: 'pending', label: t('merchants.colPendingBalance'), width: 150, align: 'right' },
              { key: 'cashIn', label: t('merchants.colCashIn'), width: 150, align: 'right' },
              { key: 'cashOut', label: t('merchants.colCashOut'), width: 150, align: 'right' },
              { key: 'api', label: t('merchants.colApi'), width: 90 },
              { key: 'status', label: t('merchants.colStatus'), width: 120 },
              { key: 'created', label: t('merchants.colCreated'), width: 150 },
              { key: 'action', width: 44, fixed: true, sticky: 'right' },
            ]}
          >
                {data?.rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={13}>
                      <Text c="dimmed" ta="center" py="lg">
                        {t('merchants.empty')}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {data?.rows.map((m) => (
                  <Table.Tr
                    key={m.id}
                    onClick={() => navigate(`/dashboard/merchants/${m.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Table.Td>
                      <Text size="xs" ff="monospace" c="dimmed">
                        {m.id.slice(0, 8)}…{m.id.slice(-4)}
                      </Text>
                    </Table.Td>
                    <Table.Td fw={500}>{m.name}</Table.Td>
                    <Table.Td>{m.email}</Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <span>{formatBps(m.depositFeeBps)}</span>
                        {m.vaFees && Object.keys(m.vaFees).length > 0 && (
                          <FeeOverrideBadge
                            accent="blue"
                            chipLabel={t('merchants.feeOverrideVa', {
                              n: Object.keys(m.vaFees).length,
                            })}
                            title={t('merchants.feeOverrideVaTitle')}
                            subtitle={t('merchants.feeOverrideVaSub')}
                            defaultLabel={t('merchants.feeOverrideVaDefault')}
                            defaultValue={formatBps(m.depositFeeBps)}
                            rows={Object.entries(m.vaFees).map(([code, f]) => ({
                              label: code,
                              pct: formatBps(f.bps),
                              amount: formatIdr(f.fixed),
                            }))}
                          />
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <span>{formatIdr(m.disbursementFeeIdr)}</span>
                        {m.disbursementFees && Object.keys(m.disbursementFees).length > 0 && (
                          <FeeOverrideBadge
                            accent="grape"
                            chipLabel={t('merchants.feeOverrideType', {
                              n: Object.keys(m.disbursementFees).length,
                            })}
                            title={t('merchants.feeOverrideDisbTitle')}
                            subtitle={t('merchants.feeOverrideDisbSub')}
                            defaultLabel={t('merchants.feeOverrideDisbDefault')}
                            defaultValue={formatIdr(m.disbursementFeeIdr)}
                            rows={Object.entries(m.disbursementFees).map(([type, f]) => ({
                              label: DISB_TYPE_LABEL[type] ?? type,
                              pct: formatBps(f.bps),
                              amount: formatIdr(f.fixed),
                            }))}
                          />
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td ta="right">{formatIdr(m.activeBalance)}</Table.Td>
                    <Table.Td ta="right">{formatIdr(m.pendingBalance)}</Table.Td>
                    <Table.Td ta="right">{formatIdr(m.totalCashIn)}</Table.Td>
                    <Table.Td ta="right">{formatIdr(m.totalCashOut)}</Table.Td>
                    <Table.Td>
                      <Badge color={m.apiAccessEnabled ? 'green' : 'gray'} variant="light">
                        {m.apiAccessEnabled ? t('merchants.apiEnabled') : t('merchants.apiDisabled')}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {m.suspendedAt ? (
                        <Badge color="red" variant="light">
                          {t('merchants.statusSuspended')}
                        </Badge>
                      ) : (
                        <Badge color="green" variant="light">
                          {t('merchants.statusActive')}
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>{formatGmt7(m.createdAt)}</Table.Td>
                    <Table.Td>
                      <IconChevronRight size={16} />
                    </Table.Td>
                  </Table.Tr>
                ))}
          </DataTable>
        )}
        <Group justify="space-between" p="md">
          <Text c="dimmed" size="xs">
            {t('wallet.pageRows', { page: pageIdx + 1, count: data?.rows.length ?? 0 })}
          </Text>
          <Group gap="xs">
            <Button
              variant="default"
              size="xs"
              disabled={pageIdx === 0}
              onClick={() => setPageIdx((i) => i - 1)}
            >
              {t('common.prev')}
            </Button>
            <Button
              variant="default"
              size="xs"
              disabled={!data?.hasMore}
              onClick={() => {
                if (data?.nextCursor) {
                  setCursorStack((s) => [...s.slice(0, pageIdx + 1), data.nextCursor]);
                  setPageIdx((i) => i + 1);
                }
              }}
            >
              {t('common.next')}
            </Button>
          </Group>
        </Group>
      </Card>
    </Stack>
  );
}
